/**
 * Um filtro do Explorar numa pílula: o botão mostra o nome e quantas opções estão ligadas, e abre o
 * painel logo abaixo dele. O painel fica fora da rolagem (posição fixa), mas logo depois do botão na
 * ordem do Tab; fecha com Esc (o foco volta ao botão), com um clique fora ou quando o foco sai dele.
 * Com `sheet` (a tela estreita), o painel é uma folha presa embaixo, no fim da página (acima de tudo),
 * sobre um fundo escuro que fecha com um toque: no alto, o nome, `actions` (o "Limpar filtros") e o
 * fechar; no pé, o botão `done`.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/index.js';
import { Icon, type IconName } from '../ui/icons.js';

export function FilterMenu({ label, icon, count, sheet = false, actions, done, children }: { label: string; icon?: IconName; count: number; sheet?: boolean; actions?: ReactNode; done?: (close: () => void) => ReactNode; children: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = () => setOpen(false);

  // abaixo do botão, sem sair da janela pela direita
  const place = () => {
    const at = button.current?.getBoundingClientRect();
    const width = panel.current?.offsetWidth ?? 0;
    if (at) setPos({ left: Math.max(8, Math.min(at.left, window.innerWidth - width - 8)), top: at.bottom + 8 });
  };
  useLayoutEffect(() => {
    if (open && !sheet) place();
    if (open) panel.current?.querySelector<HTMLElement>('input, button, [tabindex]')?.focus({ preventScroll: true });
  }, [open, sheet]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!wrap.current?.contains(target) && !panel.current?.contains(target)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
      button.current?.focus();
    };
    const onMove = () => (sheet ? undefined : place());
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, sheet]);

  return (
    <div
      ref={wrap}
      className="rd-filterMenu"
      onBlur={(e) => {
        // o foco saiu do botão e do painel (Tab adiante): fecha, como um menu
        if (!sheet && e.relatedTarget && !wrap.current?.contains(e.relatedTarget as Node)) close();
      }}
    >
      <button ref={button} type="button" className={`rd-filterPill${count ? ' is-on' : ''}${open ? ' is-open' : ''}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        {icon ? <Icon name={icon} size={16} /> : null}
        {label}
        {count ? <span className="rd-filterPill__count">{count}</span> : null}
        <Icon name="chevronDown" size={16} />
      </button>
      {open && !sheet ? (
        <div ref={panel} id={id} role="group" aria-label={label} className="rd-filterPanel" style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? undefined : 'hidden' }}>
          {children}
        </div>
      ) : null}
      {/* a folha vai para o fim da página, acima da barra de amigos e de tudo o que a página empilha */}
      {open && sheet
        ? createPortal(
            <>
              <div className="rd-filterMenu__backdrop" aria-hidden="true" onPointerDown={close} />
              <div ref={panel} id={id} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} className="rd-filterPanel rd-filterPanel--sheet">
                <div className="rd-filterPanel__head">
                  <h2 id={`${id}-title`} className="rd-filterPanel__title">
                    {label}
                  </h2>
                  {actions}
                  <button type="button" className="rd-filterPanel__close" aria-label={t('common.close')} onClick={close}>
                    <Icon name="close" size={20} />
                  </button>
                </div>
                {children}
                {done ? <div className="rd-filterPanel__done">{done(close)}</div> : null}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
